import { useMemo, useRef } from 'react';

import type { EventSearchSummary } from '@src/models/interfaces/event.interface';
import { useElasticDivisionIds } from '@src/packages/utils';
import { ALLOWED_ROLES } from '@src/packages/utils/access';
import useGetDivisionsAllowed from '@src/packages/utils/hooks/useGetDivisionsAllowed';
import EventService from '@src/services/EventService';

import { resolveSearchDivisionIds } from '../utils/searchDivisionIds';
import useDebouncedRemoteSearch from './useDebouncedRemoteSearch';

const SEARCH_PAGE_SIZE = 8;

interface UseSidebarEventSearchParams {
  enabled: boolean;
  orgId?: string;
  searchTerm: string;
}

const useSidebarEventSearch = ({ enabled, orgId, searchTerm }: UseSidebarEventSearchParams) => {
  const eventServiceRef = useRef<EventService | null>(null);
  if (eventServiceRef.current === null) {
    eventServiceRef.current = new EventService();
  }

  const { allowedDivsIds, allowedSelectedDivs } = useGetDivisionsAllowed(
    ALLOWED_ROLES.ORGANIZATIONS.DIVISIONS.EVENTS_SUMMARY.LIST,
  );
  const elasticDivIds = useElasticDivisionIds();
  const divisionIds = useMemo(
    () => resolveSearchDivisionIds(elasticDivIds, allowedDivsIds, allowedSelectedDivs),
    [elasticDivIds, allowedDivsIds, allowedSelectedDivs],
  );

  const { results, loading, error } = useDebouncedRemoteSearch<EventSearchSummary>({
    enabled: enabled && Boolean(orgId) && divisionIds !== null,
    searchTerm,
    scopeKey: `${orgId}|${divisionIds?.join(',') ?? ''}`,
    search: async normalizedTerm => {
      if (!orgId || !divisionIds) return [];
      const result = await eventServiceRef.current?.searchSummaries({
        orgId,
        divIds: divisionIds,
        searchTerm: normalizedTerm,
        pageSize: SEARCH_PAGE_SIZE,
      });
      return result?.items ?? [];
    },
  });

  return { events: results, loading, error };
};

export default useSidebarEventSearch;
